// import axios from "axios";
// import { responseInterceptor } from "./interceptos";
// import { Environment } from "../../../environment";
// import { StorageKeysEnum } from "../../../enums";

// interface IApiParams {
// 	disableResponseInterceptor: boolean;
// }

// const Api = (params?: IApiParams) => {
// 	const token = localStorage.getItem(StorageKeysEnum.AccessToken);
// 	const api = axios.create({
// 		baseURL: Environment.URL_BASE,
// 		headers: {
// 			Authorization: `Bearer ${token}`,
// 		},
// 	});

// 	if (!params?.disableResponseInterceptor) {
// 		api.interceptors.response.use(
// 			(response) => responseInterceptor(response),
// 			(error) => {
// 				console.dir(error?.response);
// 				const originalRequest = error.config;

// 				if (error.response.status === 401 && !originalRequest._retry) {
// 					originalRequest._retry = true;
// 					const token: string = localStorage.getItem(StorageKeysEnum.AccessToken) || "";
// 					return refreshAccessToken(token)
// 						.then((accessToken) => {
// 							originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
// 							return api(originalRequest);
// 						})
// 						.catch(() => {
// 							localStorage.clear();
// 							window.location.assign(window.location.protocol + "//" + window.location.host + "/login");
// 						});
// 				}

// 				return Promise.reject(error);
// 			}
// 		);
// 	}

// 	return api;
// };

// const ApiSignature = {}

// function refreshAccessToken(refreshToken: string): Promise<string> {
// 	// Send a request to the server to refresh the access token using the refresh token
// 	return axios
// 		.put(
// 			`${Environment.URL_BASE}auth/refresh`,
// 			{ refreshToken },
// 			{
// 				headers: {
// 					Authorization: `Bearer ${refreshToken}`,
// 					//'Authorization': `${token}`
// 				},
// 			}
// 		)
// 		.then((response) => response.data.accessToken);
// }
// export { Api };

// src/shared/services/api/index.ts
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { responseInterceptor } from "./interceptos";
import { Environment } from "../../../environment";
import { StorageKeysEnum } from "../../../enums";

// --- TS: estender AxiosRequestConfig para suportar _retry
declare module "axios" {
	export interface AxiosRequestConfig {
		_retry?: boolean;
	}
}

// Como é React SPA, window existe; mas manter pequenos guards não atrapalha:
const isBrowser = typeof window !== "undefined";

function safeGetLocalStorage(key: string): string | null {
	try {
		return isBrowser ? window.localStorage.getItem(key) : null;
	} catch {
		return null;
	}
}
function safeSetLocalStorage(key: string, value: string) {
	try {
		if (isBrowser) window.localStorage.setItem(key, value);
	} catch {
		/* noop */
	}
}
function safeClearSessionAndRedirectToLogin() {
	try {
		if (isBrowser) {
			window.localStorage.removeItem(StorageKeysEnum.AccessToken);
			window.location.assign(`${window.location.protocol}//${window.location.host}/login`);
		}
	} catch {
		/* noop */
	}
}

// --------- Util: Token Storage ----------
function getAccessToken(): string | null {
	return safeGetLocalStorage(StorageKeysEnum.AccessToken);
}
function setAccessToken(token: string) {
	safeSetLocalStorage(StorageKeysEnum.AccessToken, token);
}

// --------- Refresh lock/queue (anti stampede) ----------
let isRefreshing = false;
let pendingQueue: Array<(token: string) => void> = [];
let pendingRejects: Array<(err: unknown) => void> = [];

function subscribeTokenRefresh(resolve: (token: string) => void, reject: (err: unknown) => void) {
	pendingQueue.push(resolve);
	pendingRejects.push(reject);
}
function onRefreshed(token: string) {
	pendingQueue.forEach((cb) => cb(token));
	pendingQueue = [];
	pendingRejects = [];
}
function onRefreshError(err: unknown) {
	pendingRejects.forEach((rej) => rej(err));
	pendingQueue = [];
	pendingRejects = [];
}

// --------- Refresh impl ----------
function refreshAccessToken(refreshToken: string): Promise<string> {
	// Se existir AUTH_BASE separado, use; senão use URL_BASE
	const AUTH_BASE = (Environment as any).AUTH_BASE ?? Environment.URL_BASE ?? "";
	return axios
		.put(
			`${AUTH_BASE}auth/refresh`,
			{ refreshToken },
			{
				headers: {
					// Ajuste se seu backend NÃO espera Authorization no refresh:
					Authorization: `Bearer ${refreshToken}`,
				},
			}
		)
		.then((res) => res.data.accessToken as string);
}

// --------- Fábrica de API client ----------
export interface IApiParams {
	disableResponseInterceptor?: boolean;
	axiosConfig?: AxiosRequestConfig; // timeout, headers extras etc.
}

function createApiClient(baseURL: string, params?: IApiParams): AxiosInstance {
	const instance = axios.create({
		baseURL,
		...(params?.axiosConfig ?? {}),
	});

	// Request: injeta token "fresh" a cada chamada
	instance.interceptors.request.use((config) => {
		const token = getAccessToken();
		if (token) {
			(config.headers = config.headers ?? {})["Authorization"] = `Bearer ${token}`;
		}
		return config;
	});

	if (!params?.disableResponseInterceptor) {
		instance.interceptors.response.use(
			(response) => responseInterceptor(response),
			async (error) => {
				const originalRequest = error?.config as AxiosRequestConfig | undefined;
				const status = error?.response?.status;

				if (status !== 401 || !originalRequest || originalRequest._retry) {
					return Promise.reject(error);
				}

				originalRequest._retry = true;

				// Se já há um refresh em andamento, fila a tentativa
				if (isRefreshing) {
					return new Promise((resolve, reject) => {
						subscribeTokenRefresh(
							(newToken) => {
								originalRequest.headers = originalRequest.headers ?? {};
								originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
								resolve(instance(originalRequest));
							},
							(err) => reject(err)
						);
					});
				}

				isRefreshing = true;
				const currentToken = getAccessToken() ?? "";

				try {
					const newAccessToken = await refreshAccessToken(currentToken);
					setAccessToken(newAccessToken);
					onRefreshed(newAccessToken);

					originalRequest.headers = originalRequest.headers ?? {};
					originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
					return instance(originalRequest);
				} catch (err) {
					onRefreshError(err);
					safeClearSessionAndRedirectToLogin();
					return Promise.reject(err);
				} finally {
					isRefreshing = false;
				}
			}
		);
	}

	return instance;
}

// --------- APIs exportadas (compatível + nova) ----------
const Api = (params?: IApiParams) => createApiClient(Environment.URL_BASE ?? "", params);
const ApiSignature = (params?: IApiParams) => createApiClient(Environment.URL_SIGNATURE_BASE ?? "", params);

export { Api, ApiSignature };
